import { useState, useEffect } from 'react';
import { AlertTriangle, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { fetchConflictLog, clearConflictLog, type ConflictLogEntry } from '@/hooks/use-offline';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

interface ConflictLogProps {
  visible: boolean;
  onClose: () => void;
}

function getResolutionBadge(resolution: string) {
  switch (resolution) {
    case 'local-wins':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          Local Applied
        </span>
      );
    case 'server-wins':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Server Kept
        </span>
      );
    case 'discarded':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Discarded
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          Failed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          {resolution}
        </span>
      );
  }
}

export function ConflictLog({ visible, onClose }: ConflictLogProps) {
  const orgTimeZone = useOrgTimeZone();
  const [entries, setEntries] = useState<ConflictLogEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    fetchConflictLog()
      .then((data) => { if (!cancelled) setEntries(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visible]);

  const handleClear = () => {
    clearConflictLog();
    setEntries([]);
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold">Offline Sync Conflicts</h2>
          </div>
          <div className="flex items-center gap-2">
            {entries.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Clear All
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close conflict log"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No sync conflicts recorded.</p>
              <p className="text-sm mt-1">Conflicts appear here when offline changes can&apos;t be synced cleanly.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="border rounded-lg overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(entry.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {getResolutionBadge(entry.resolution)}
                      <span className="text-sm font-medium truncate">
                        {entry.method} {new URL(entry.url, window.location.origin).pathname}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {formatDateTimeInTimeZone(
                          new Date(entry.resolvedAt).toISOString(),
                          orgTimeZone
                        )}
                      </span>
                      {expanded.has(entry.id) ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                  {expanded.has(entry.id) && (
                    <div className="px-4 py-3 bg-gray-50 border-t text-sm">
                      <p className="text-gray-700">{entry.detail}</p>
                      <div className="mt-2 flex gap-4 text-xs text-gray-500">
                        <span>
                          Queued:{' '}
                          {formatDateTimeInTimeZone(
                            new Date(entry.queuedAt).toISOString(),
                            orgTimeZone
                          )}
                        </span>
                        <span>
                          Resolved:{' '}
                          {formatDateTimeInTimeZone(
                            new Date(entry.resolvedAt).toISOString(),
                            orgTimeZone
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-500">
          {entries.length} conflict{entries.length !== 1 ? 's' : ''} recorded
        </div>
      </div>
    </div>
  );
}
